import UserService from "api/user/user";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { LAST_VISIT } from "v4/data/apiRoutes";
import { CHECKBOX_TYPE } from "v4/data/fieldTypes";
import { useNavigation } from "v4/features/front/navigation/contexts/NavigationContext";
import useFetch from "v4/hooks/useFetch";
import useToggle from "v4/hooks/useToggle";
import { generateUrl } from "v4/services/api.service";

const defaultFront = {
    backgroundListing: null,
    activeListingRoute: null,
    goBackToListing: () => {
    },
    a11yValues: {},
    a11yForm: null,
    updateA11y: () => {
    },
    lastVisits: [],
    setLastVisits: () => {
    },
    addLastVisit: () => {
    },
    refetchLastsVisits: false,
    toggleRefetchLastsVisits: () => {
    },
}

export const FrontContext = createContext(defaultFront)

export function useFrontContext() {
    return useContext(FrontContext)
}

export function FrontProvider({ body, header, footer }) {
    const { t } = useTranslation();
    const { push } = useHistory();
    const { currentRoute, mainNavigation, getRouteByNativeIdentifier } = useNavigation();
    const { isDetail } = currentRoute;

    // --- 1. Persistance du Listing en arrière-plan ---
    const [backgroundListing, setBackgroundListing] = useState(null);

    // Mise à jour du listing uniquement quand on est sur un vrai listing de premier niveau
    useEffect(() => {
        const LISTING_TYPES = ['LISTING', 'ALTERNATIVE_LISTING', 'ADD', 'EDIT', 'ADD_BUSINESS', 'ADD_INDIVIDUAL', 'CUSTOMER_CODE'];

        // On ignore les détails et surtout la route 404 (*)
        if (isDetail || !LISTING_TYPES.includes(currentRoute?.routeType) || currentRoute?.route?.native_identifier === 'main_not_found') return;

        // On ignore les groupes
        if (!currentRoute?.route?.native_identifier && !currentRoute?.route?.search_type) return;

        setBackgroundListing(currentRoute);
    }, [currentRoute, isDetail]);

    // --- 2. Action de retour ---
    const [clickedId, setClickedId] = useState(null);

    const saveClickedId = useCallback((e) => {
        const id = e.currentTarget.id;
        if (id) setClickedId(id);
    }, []);

    const clearClickedId = useCallback(() => setClickedId(null), []);

    const goBackToListing = useCallback(() => {
        let url = backgroundListing?.route?.url;

        if (!url && mainNavigation) {
            url = getRouteByNativeIdentifier('main_prospects')?.url;
        }

        if (url) {
            let finalUrl = url.startsWith('/') ? url : `/${url}`;

            // Détection dynamique de l'ID pour le pulse
            // On cherche un ID dans les params qui correspond à l'un de nos types d'entités connus
            const entityParams = ['prospect', 'contact', 'task', 'quote', 'article', 'mailType'];
            const paramId = entityParams.map(p => currentRoute?.params?.[p]).find(val => !!val);
            
            const targetId = clickedId || paramId || currentRoute?.groupId;

            if (targetId) {
                finalUrl += `#${targetId}`;
            }

            return push(finalUrl);
        }

        return push('/');
    }, [backgroundListing, mainNavigation, getRouteByNativeIdentifier, push, clickedId, currentRoute]);

    // --- 3. Gestion des dernières visites (Last Visits) ---
    const [lastVisits, setLastVisits] = useState(defaultFront.lastVisits);
    const [refetchLastsVisits, toggleRefetchLastsVisits] = useToggle(defaultFront.refetchLastsVisits);
    const [, fetchLastVisits] = useFetch();

    const addLastVisit = useCallback((item) => {
        const hasSameUrl = visit => visit.url === item.url;
        const removeDuplicates = visit => !hasSameUrl(visit);

        fetchLastVisits({
            url: generateUrl(LAST_VISIT),
            config: {
                method: 'POST',
                body: JSON.stringify(item)
            }
        });

        setLastVisits(prev => {
            const alreadyExistsAtIndex = prev.findIndex(hasSameUrl);
            if (alreadyExistsAtIndex === 0) return prev;

            return [item, ...prev.filter(removeDuplicates)].slice(0, 10);
        });
    }, [fetchLastVisits]);

    // --- 4. Accessibilité (A11y) ---
    const [a11yValues, setA11yValues] = useState(UserService.getA11ySettings());
    const updateA11y = useCallback((newValues) => {
        const updatedA11yValues = { ...a11yValues, ...newValues };
        setA11yValues(updatedA11yValues);
        UserService.setA11ySettings(updatedA11yValues);
    }, [a11yValues]);

    const a11yForm = {
        type: "success:get-form-schema",
        data: {
            "contrasted_table": {
                label: t('contrasted_table'),
                name: "contrasted_table",
                type: CHECKBOX_TYPE,
                value: a11yValues['contrasted_table'] ?? false,
            }
        }
    };

    // --- 5. Valeurs du contexte ---
    const valueToRender = useMemo(() => {
        let activeRoute = backgroundListing?.route || null;

        // Si on n'a pas de listing (refresh sur détail), on cherche le menu Prospects par défaut
        if (!activeRoute && isDetail) {
            activeRoute = getRouteByNativeIdentifier('main_prospects');
        }

        return {
            // Layering & Routing
            backgroundListing,
            activeListingRoute: activeRoute,
            goBackToListing,
            clickedId,
            saveClickedId,
            clearClickedId,

            // A11y
            a11yValues,
            a11yForm,
            updateA11y,

            // Last Visits
            lastVisits,
            setLastVisits,
            addLastVisit,
            refetchLastsVisits,
            toggleRefetchLastsVisits
        };
    }, [backgroundListing, isDetail, getRouteByNativeIdentifier, goBackToListing, clickedId, saveClickedId, clearClickedId, a11yValues, a11yForm, updateA11y, lastVisits, addLastVisit, refetchLastsVisits, toggleRefetchLastsVisits]);

    return (
        <FrontContext.Provider value={valueToRender}>
            {header?.()}
            {body}
            {footer?.()}
        </FrontContext.Provider>
    );
}
